import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { BakingSession } from '../../types/baking';
import { SessionCard } from './SessionCard';
import { SessionDetailsModal } from './SessionDetailsModal';

interface ActiveBakesProps {
  sessions: BakingSession[];
  onSessionCancel: (sessionId: string) => Promise<void>;
}

export function ActiveBakes({ sessions, onSessionCancel }: ActiveBakesProps) {
  const [selectedSession, setSelectedSession] = useState<BakingSession | null>(null);
  const [isActiveExpanded, setIsActiveExpanded] = useState(false);
  const [isPlannedExpanded, setIsPlannedExpanded] = useState(false);

  const now = new Date();
  
  // Filter active sessions (in progress or scheduled within next 24 hours)
  const activeSessions = sessions.filter(session => {
    if (session.status !== 'scheduled' && session.status !== 'in-progress') return false;
    const targetTime = new Date(session.targetBakeTime);
    return session.status === 'in-progress' || 
           (targetTime.getTime() - now.getTime() <= 24 * 60 * 60 * 1000);
  });

  // Filter planned sessions (scheduled beyond 24 hours)
  const plannedSessions = sessions.filter(session => {
    if (session.status !== 'scheduled') return false;
    const targetTime = new Date(session.targetBakeTime);
    return targetTime.getTime() - now.getTime() > 24 * 60 * 60 * 1000;
  });

  // Split active sessions into visible and hidden
  const visibleActiveSessions = activeSessions.slice(0, 2);
  const hiddenActiveSessions = activeSessions.slice(2);
  const hasHiddenActiveSessions = hiddenActiveSessions.length > 0;

  // Split planned sessions into visible and hidden
  const visiblePlannedSessions = plannedSessions.slice(0, 2);
  const hiddenPlannedSessions = plannedSessions.slice(2);
  const hasHiddenPlannedSessions = hiddenPlannedSessions.length > 0;

  const handleSessionUpdate = async (sessionId: string) => {
    await onSessionCancel(sessionId);
    setSelectedSession(null);
  };

  return (
    <div className="p-1">
      <div className="space-y-6">
        {/* Active Bakes Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-2xl text-white font-brand font-light italic">Active Bakes</h2>
          </div>
          
          <div className="space-y-3">
            {activeSessions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No active baking sessions</p>
            ) : (
              <>
                {/* Always visible sessions */}
                {visibleActiveSessions.map(session => (
                  <SessionCard 
                    key={session.id} 
                    session={session}
                    onClick={() => setSelectedSession(session)}
                    variant="active"
                  />
                ))}

                {/* Collapsible section for additional active sessions */}
                {hasHiddenActiveSessions && (
                  <div>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out space-y-3 ${
                        isActiveExpanded ? 'max-h-[1000px]' : 'max-h-0'
                      }`}
                    >
                      {hiddenActiveSessions.map(session => (
                        <SessionCard 
                          key={session.id} 
                          session={session}
                          onClick={() => setSelectedSession(session)}
                          variant="active"
                        />
                      ))}
                    </div>

                    {/* Chevron button */}
                    <button
                      onClick={() => setIsActiveExpanded(!isActiveExpanded)}
                      className="w-full h-6 flex items-center justify-center text-white/70 hover:text-amber-400 transition-colors"
                    >
                      <ChevronDown 
                        className={`w-6 h-6 transition-transform duration-300 ${
                          isActiveExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Planned Sessions Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl text-white font-brand font-light italic">Planned Sessions</h2>
          </div>
          
          {plannedSessions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No planned sessions</p>
          ) : (
            <div className="space-y-3">
              {/* Always visible planned sessions */}
              {visiblePlannedSessions.map(session => (
                <SessionCard 
                  key={session.id} 
                  session={session}
                  onClick={() => setSelectedSession(session)}
                  variant="planned"
                />
              ))}

              {/* Collapsible section for additional planned sessions */}
              {hasHiddenPlannedSessions && (
                <div>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out space-y-3 ${
                      isPlannedExpanded ? 'max-h-[1000px]' : 'max-h-0'
                    }`}
                  >
                    {hiddenPlannedSessions.map(session => (
                      <SessionCard 
                        key={session.id} 
                        session={session}
                        onClick={() => setSelectedSession(session)}
                        variant="planned"
                      />
                    ))}
                  </div>

                  {/* Chevron button */}
                  <button
                    onClick={() => setIsPlannedExpanded(!isPlannedExpanded)}
                    className="w-full h-6 flex items-center justify-center text-white/70 hover:text-amber-400 transition-colors"
                  >
                    <ChevronDown 
                      className={`w-6 h-6 transition-transform duration-300 ${
                        isPlannedExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SessionDetailsModal
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onCancel={handleSessionUpdate}
      />
    </div>
  );
}